import React, { useEffect, useState } from "react";
import { Map, Marker, useMap } from "@vis.gl/react-google-maps";
import { TAFT_CENTER, useGoogleMapsStatus } from "./GoogleMapsProvider";
import api from "../api";
import { toast } from "sonner";
import { browserGeocode } from "../lib/browserGeocode";

/**
 * Forces a Google Map to recompute its size after the modal mounts.
 * Without this, maps inside modals often render with 0×0 tiles or a
 * blank grey background until the user manually resizes the window.
 */
function MapResizeFix({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const fire = () => {
      try {
        window.google?.maps?.event?.trigger(map, "resize");
        if (target) map.panTo({ lat: target[0], lng: target[1] });
      } catch { /* noop */ }
    };
    const t1 = setTimeout(fire, 60);
    const t2 = setTimeout(fire, 350);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [map, target ? target[0] : null, target ? target[1] : null]);
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !target) return;
    map.panTo({ lat: target[0], lng: target[1] });
    map.setZoom(16);
  }, [map, target ? target[0] : null, target ? target[1] : null]);
  return null;
}

function ClickToPlace({ onPick }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("click", (e) => {
      if (!e.latLng) return;
      onPick([e.latLng.lat(), e.latLng.lng()]);
    });
    return () => listener.remove();
  }, [map, onPick]);
  return null;
}

export default function MapPickerModal({
  initialLat,
  initialLng,
  initialAddress = "",
  onClose,
  onPick,
}) {
  const mapsStatus = useGoogleMapsStatus();
  const [pos, setPos] = useState(
    initialLat && initialLng ? [Number(initialLat), Number(initialLng)] : null
  );
  const [searchTerm, setSearchTerm] = useState(initialAddress || "");
  const [searching, setSearching] = useState(false);
  const [flyTarget, setFlyTarget] = useState(
    initialLat && initialLng ? [Number(initialLat), Number(initialLng)] : null
  );

  const defaultCenter = initialLat && initialLng
    ? { lat: Number(initialLat), lng: Number(initialLng) }
    : TAFT_CENTER;

  async function geocodeSearch() {
    if (!searchTerm.trim()) return;
    setSearching(true);
    try {
      let hit = await browserGeocode(searchTerm);
      if (!hit) {
        const { data } = await api.post("/geocode", { address: searchTerm.trim() });
        if (data.ok) hit = data;
      }
      if (hit) {
        const p = [hit.lat, hit.lng];
        setPos(p);
        setFlyTarget(p);
      } else {
        toast.error("Address not found — try adding city/ZIP or drop the pin by clicking the map");
      }
    } catch {
      toast.error("Address search failed — drop the pin by clicking the map");
    } finally {
      setSearching(false);
    }
  }

  function useMyLocation() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (g) => {
        const p = [g.coords.latitude, g.coords.longitude];
        setPos(p);
        setFlyTarget(p);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  function confirm() {
    if (!pos) return;
    onPick(pos[0], pos[1]);
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-3xl rounded-sm border border-[#012340]/15 overflow-hidden flex flex-col"
        style={{ maxHeight: "90vh" }}
        data-testid="map-picker"
      >
        <div className="px-5 py-4 border-b border-[#012340]/10 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">
              Place pin manually
            </div>
            <div className="font-heading text-lg font-bold text-[#012340]">
              Click the map to set the customer&apos;s location
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-sm hover:bg-[#012340]/5">
            ✕
          </button>
        </div>
        <div className="px-5 py-3 border-b border-[#012340]/10 flex flex-wrap gap-2 items-center">
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                geocodeSearch();
              }
            }}
            placeholder="Search an address to fly to…"
            className="flex-1 min-w-[200px] rounded-sm bg-white border border-[#012340]/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]"
            data-testid="picker-search"
          />
          <button
            onClick={geocodeSearch}
            disabled={searching}
            className="px-3 py-2 rounded-sm bg-[#012340] text-white text-sm font-bold disabled:opacity-50"
            data-testid="picker-search-btn"
          >
            {searching ? "Searching…" : "Search"}
          </button>
          <button
            onClick={useMyLocation}
            className="px-3 py-2 rounded-sm border border-[#012340]/20 text-[#012340] text-sm font-bold"
            data-testid="picker-me"
          >
            Use my location
          </button>
        </div>
        <div style={{ height: 420, minHeight: 420, width: "100%" }}>
          {mapsStatus.apiKeyMissing || mapsStatus.authFailed ? (
            <div className="w-full h-full grid place-items-center bg-[#FAFAFA] text-center px-6" data-testid="picker-map-fallback">
              <div>
                <div className="text-[#D32F2F] font-bold text-sm mb-1">Map failed to load.</div>
                <div className="text-[#5C738A] text-xs max-w-md">
                  Please check Google Maps API configuration
                  {mapsStatus.apiKeyMissing
                    ? " (REACT_APP_GOOGLE_MAPS_API_KEY is missing)."
                    : " (HTTP referrer restrictions, billing, or enabled APIs)."}
                </div>
                <div className="mt-3 text-[#012340] text-xs font-mono">
                  {pos ? `📍 ${pos[0].toFixed(6)}, ${pos[1].toFixed(6)}` : "No pin selected."}
                </div>
                <div className="mt-3 text-[11px] text-[#5C738A]">
                  You can still enter latitude / longitude manually in the customer form.
                </div>
              </div>
            </div>
          ) : (
            <Map
              defaultCenter={defaultCenter}
              defaultZoom={pos ? 15 : 11}
              gestureHandling="greedy"
              disableDefaultUI={false}
              mapTypeControl={false}
              streetViewControl={false}
              fullscreenControl={false}
              style={{ width: "100%", height: "100%" }}
            >
              <MapResizeFix target={pos || flyTarget} />
              <ClickToPlace onPick={(p) => setPos(p)} />
              {pos && (
                <Marker position={{ lat: pos[0], lng: pos[1] }} />
              )}
              <FlyTo target={flyTarget} />
            </Map>
          )}
        </div>
        <div className="px-5 py-3 border-t border-[#012340]/10 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs text-[#5C738A] font-mono">
            {pos
              ? `📍 ${pos[0].toFixed(6)}, ${pos[1].toFixed(6)}`
              : "Click anywhere on the map…"}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold"
            >
              Cancel
            </button>
            <button
              onClick={confirm}
              disabled={!pos}
              className="btn-primary disabled:opacity-50"
              data-testid="picker-confirm"
            >
              Use this location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
